import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import * as AppSettings from './AppSettings';
import * as AuditWorkpapers from './AuditWorkpapers';
import * as Borrower from './Borrower';
import * as FieldGuide from './FieldGuide';
import * as FinancialStatements from './FinancialStatements';
import * as GlobalSettings from './GlobalSettings';
import * as ImportFinancials from './ImportFinancials';
import * as Industries from './Industries';
import * as MultiTenant from './MultiTenant';
import * as NewDataCollectionParameters from './NewDataCollectionParameters';
import * as PortalAssets from './PortalAssets';
import * as RunAuditWorkpapers from './RunAuditWorkpapers';
import * as RunningImportsState from './RunningImports';
import * as Submission from './Submission';
import * as SubmissionHistory from './SubmissionHistory';
import * as Tenant from './Tenant';
import * as UIState from './UIState';
import * as UserState from './User';
import * as UserRoles from './UserRoles';
import * as UserSettingsState from './UserSettings';
import * as Workspaces from './Workspaces';

// The top-level state object
export interface ApplicationState {
    appSettings: AppSettings.IAppSettingsState;
    auditWorkpapers: AuditWorkpapers.IAuditWorkpapersState;
    borrower: Borrower.IBorrowerState;
    industries: Industries.IIndustriesState;
    newDataCollectionParameters: NewDataCollectionParameters.INewDataCollectionParametersState;
    tenant: Tenant.ITenantState;
    uiState: UIState.IUIState;
    runningImportsState: RunningImportsState.IRunningImportsState;
    userState: UserState.IUserState;
    userSettingsState: UserSettingsState.IUserSettingsState;
    userRolesState: UserRoles.IUserRolesState;
    submissionHistory: SubmissionHistory.ISubmissionHistoryState;
    workspaces: Workspaces.IWorkspacesState;
    importFinancials: ImportFinancials.IImportFinancialsState;
    globalSettings: GlobalSettings.IGlobalSettingsState;
    financialStatements: FinancialStatements.IFinancialStatementsState;
    submission: Submission.ISubmissionState;
    portalAssets: PortalAssets.IPortalAssetsState;
    multiTenant: MultiTenant.IMultiTenantState;
    runAuditWorkpapers: RunAuditWorkpapers.IRunAuditWorkpapersState;
    fieldGuide: FieldGuide.IFieldGuideState;
}

// Whenever an action is dispatched, Redux will update each top-level application state property using
// the reducer with the matching name. It's important that the names match exactly, and that the reducer
// acts on the corresponding ApplicationState property type.
export const reducers = {
    appSettings: AppSettings.reducer,
    auditWorkpapers: AuditWorkpapers.reducer,
    borrower: Borrower.reducer,
    industries: Industries.reducer,
    newDataCollectionParameters: NewDataCollectionParameters.reducer,
    tenant: Tenant.reducer,
    uiState: UIState.reducer,
    runningImportsState: RunningImportsState.reducer,
    userState: UserState.reducer,
    userSettingsState: UserSettingsState.reducer,
    userRolesState: UserRoles.reducer,
    submissionHistory: SubmissionHistory.reducer,
    workspaces: Workspaces.reducer,
    importFinancials: ImportFinancials.reducer,
    globalSettings: GlobalSettings.reducer,
    financialStatements: FinancialStatements.reducer,
    submission: Submission.reducer,
    portalAssets: PortalAssets.reducer,
    multiTenant: MultiTenant.reducer,
    runAuditWorkpapers: RunAuditWorkpapers.reducer,
    fieldGuide: FieldGuide.reducer,
};

export type SimplifiedThunkDispatch<A extends Action> = ThunkDispatch<ApplicationState, unknown, A>

// This type can be used as a hint on action creators so that its 'dispatch' and 'getState' params are
// correctly typed to match your store.
export type AppThunkActionAsync<
    R, // Return type of the thunk function
    A extends Action // known type of actions that can be dispatched.
> = (dispatch: SimplifiedThunkDispatch<A>, getState: () => ApplicationState) => Promise<R>

// Synchronous counterpart to AppThunkAction. Use for thunks that need 'getState' to read store
// values but complete synchronously (no awaiting), so they don't have to return a Promise.
export type AppThunkAction<
    R, // Return type of the thunk function
    A extends Action // known types of actions that can be dispatched.
> = (dispatch: SimplifiedThunkDispatch<A>, getState: () => ApplicationState) => R
